import { useCallback } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { useBackgroundJobs } from 'App/settings/jobs/hooks';
import { useAppDispatch } from 'App/store';
import { useListFilters } from 'commons/hooks';
import { Format, dateFormat } from 'commons/utils/date';
import {
  ExportFiltersType,
  PIPE_SEPARATOR,
  COMMA_SEPARATOR,
} from 'domains/Orders/Orders/ordersService';
import {
  cleanExportOrders,
  exportOrders as exportOrdersAction,
  exportOrdersDownloadStatus,
} from 'domains/Orders/Orders/ordersSlice';
import { defaultFilters } from '../useOrdersExport';

interface ExportRequestPayload extends ExportFiltersType {
  couponIds?: string[];
}
function useDownloadOrdersCsv() {
  const dispatch = useAppDispatch();
  const { filters } = useListFilters('orders/export', defaultFilters);
  const downloadStatus = useSelector(exportOrdersDownloadStatus);
  const { setJob, jobStatus, csvDownloadLinkUrl, cleanJob } =
    useBackgroundJobs('ordersCsvDownload');

  const exportOrders = useCallback(async () => {
    const dateFrom = filters.dateFrom;
    const dateTo = filters.dateTo;
    const formattedDateFrom =
      dateFrom === '' ? dateFrom : dateFormat(dateFrom, Format.DD_MM_YYYY);
    const formattedDateTo =
      dateTo === '' ? dateTo : dateFormat(dateTo, Format.DD_MM_YYYY);

    // Convert pipe separator to comma for payment provider before sending to backend
    // (paymentProvider uses pipe to avoid conflicts with commas in provider IDs)
    const paymentProvider = filters.paymentProvider
      ? filters.paymentProvider.replace(
          new RegExp(`\\${PIPE_SEPARATOR}`, 'g'),
          COMMA_SEPARATOR,
        )
      : filters.paymentProvider;

    const payload: ExportRequestPayload = {
      ...filters,
      dateFrom: formattedDateFrom,
      dateTo: formattedDateTo,
      paymentProvider,
      couponIds:
        filters.couponIdsRaw === '' || filters.couponIdsRaw === undefined
          ? []
          : filters.couponIdsRaw.split(','),
    };

    const { token } = unwrapResult(await dispatch(exportOrdersAction(payload)));
    dispatch(cleanExportOrders());
    setJob(String(token));
  }, [dispatch, filters, setJob]);

  const isExporting = downloadStatus === 'loading' || jobStatus === 'pending';

  const getJobCsvDownloadStatus = () => {
    const isFailed = downloadStatus === 'error' || jobStatus === 'failed';

    const isSuccess = jobStatus === 'ready' || jobStatus === 'downloaded';

    if (isExporting) return 'loading';
    else if (isFailed) return 'error';
    else if (isSuccess) return 'success';
    else return 'idle';
  };

  const resetExportingStatus = useCallback(() => {
    dispatch(cleanExportOrders());
    cleanJob();
  }, [cleanJob, dispatch]);

  return {
    isExporting,
    jobCsvDownloadStatus: getJobCsvDownloadStatus(),
    csvDownloadLinkUrl,
    resetExportingStatus,
    exportOrders,
  };
}

export default useDownloadOrdersCsv;
